/**
 * AgendaContextMenu — URS-F252
 * Floating menu shown on right-click on an empty agenda slot.
 * Opciones: Nueva cita aqui / Bloquear este hueco.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CalendarPlus, Lock } from '@phosphor-icons/react';

export default function AgendaContextMenu({ open, x, y, date, time, onClose, onNewAppt, onBlock, lang }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    setTimeout(() => {
      document.addEventListener('mousedown', close);
      document.addEventListener('keydown', onEsc);
    }, 0);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onClose]);

  if (!open) return null;

  const lb = lang === 'es'
    ? { new_appt: 'Nueva cita aqui', block: 'Bloquear este hueco', at: 'a las' }
    : { new_appt: 'New appointment here', block: 'Block this slot', at: 'at' };

  // Clamp inside viewport
  const w = 220, h = 90;
  const vw = window.innerWidth, vh = window.innerHeight;
  const left = Math.max(8, Math.min(x, vw - w - 8));
  const top = Math.max(8, Math.min(y, vh - h - 8));

  return createPortal(
    <div
      ref={ref}
      data-testid="agenda-context-menu"
      className="fixed z-[100] bg-white border border-stone-300 rounded-sm shadow-lg py-1 min-w-[220px]"
      style={{ left, top }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-1 text-[11px] text-stone-500 border-b border-stone-100 font-mono">
        {date} {lb.at} {time}
      </div>
      <button
        type="button"
        data-testid="ctxmenu-new-appt"
        onClick={() => { onNewAppt(date, time); onClose(); }}
        className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-[#1f3e80]/5 flex items-center gap-2"
      >
        <CalendarPlus size={16} className="text-[#1f3e80]" />
        {lb.new_appt}
      </button>
      <button
        type="button"
        data-testid="ctxmenu-block"
        onClick={() => { onBlock(date, time); onClose(); }}
        className="w-full text-left px-3 py-2 text-sm text-stone-700 hover:bg-red-50 flex items-center gap-2"
      >
        <Lock size={16} className="text-red-600" />
        {lb.block}
      </button>
    </div>,
    document.body,
  );
}
